import routes from "@/routes/routes";
import React from "react";
import { checkModuleAccess, checkRole } from "./utils";

interface RouteSidebar {
  show: boolean;
  icon?: React.ReactNode;
  name?: string;
}

interface RouteConfig {
  path: string;
  element?: React.ReactNode;
  pageName?: string;
  permissionName?: string;
  requiredModule?: string;
  requiredRole?: string;
  sidebar?: RouteSidebar;
  children?: Record<string, RouteConfig>;
}

export interface SidebarChild {
  id: string;
  path: string;
  icon?: React.ReactNode | undefined;
  pageName?: string | undefined;
  permissionName?: string | undefined;
  requiredModule?: string | undefined;
  requiredRole?: string | undefined;
}

export interface SidebarItem {
  id: string;
  path: string;
  icon?: React.ReactNode | undefined;
  pageName?: string | undefined;
  permissionName?: string | undefined;
  requiredModule?: string | undefined;
  requiredRole?: string | undefined;
  children?: SidebarChild[];
}

// Type guard for sidebar
const hasSidebar = (
  pageData: any
): pageData is {
  sidebar?: { show?: boolean; icon?: React.ReactNode; name?: string };
} => pageData && "sidebar" in pageData && typeof pageData.sidebar === "object";

// Type guard for children
const hasChildren = (
  pageData: any
): pageData is { children: Record<string, any> } =>
  pageData && typeof pageData.children === "object";

/**
 * Build sidebar navigation items from routes.
 * Called as a function so it re-evaluates permissions each time (e.g., after login).
 */
export const getSideBarNavigation = (): SidebarItem[] => {
  return Object.values(routes.privateRoutes as Record<string, RouteConfig>)
    .filter((page) => {
      // Must have sidebar.show=true or children
      if (!page.sidebar?.show && !page.children) return false;
      // If a requiredModule is set, user must have at least one permission in that module
      if (page.requiredModule && !checkModuleAccess(page.requiredModule)) return false;
      // If a requiredRole is set, user must have that role
      if (page.requiredRole && !checkRole(page.requiredRole)) return false;
      return true;
    })
    .map((pageData, index) => {
      const data: SidebarItem = {
        id: (index + 1).toString(),
        path: pageData.path,
        icon: pageData.sidebar?.icon,
        pageName: pageData.sidebar?.name || pageData.pageName,
        permissionName: pageData.permissionName,
        requiredModule: pageData.requiredModule,
      };

      if (pageData.children) {
        data.children = Object.values(pageData.children)
          .filter((child) => {
            if (!child.sidebar?.show) return false;
            if (child.requiredModule && !checkModuleAccess(child.requiredModule)) return false;
            if (child.requiredRole && !checkRole(child.requiredRole)) return false;
            return true;
          })
          .map((child, childIndex) => ({
            id: `${index + 1}-${childIndex + 1}`, // unique ID
            path: child.path,
            icon: child.sidebar?.icon,
            pageName: child.sidebar?.name || child.pageName,
            permissionName: child.permissionName,
            requiredModule: child.requiredModule,
          }));
      }

      return data;
    });
};

/** @deprecated Use getSideBarNavigation() instead — kept for backward compat */
export const sideBarNavigation: SidebarItem[] = Object.values(
  routes.privateRoutes as Record<string, RouteConfig>
)
  .filter((page) => page.sidebar?.show || page.children)
  .map((pageData, index) => {
    const data: SidebarItem = {
      id: (index + 1).toString(),
      path: pageData.path,
      icon: pageData.sidebar?.icon,
      pageName: pageData.sidebar?.name || pageData.pageName,
      permissionName: pageData.permissionName,
      requiredModule: pageData.requiredModule,
    };

    if (pageData.children) {
      data.children = Object.values(pageData.children)
        .filter((child) => child.sidebar?.show)
        .map((child, childIndex) => ({
          id: `${index + 1}-${childIndex + 1}`, // unique ID
          path: child.path,
          icon: child.sidebar?.icon,
          pageName: child.sidebar?.name || child.pageName,
          permissionName: child.permissionName,
          requiredModule: child.requiredModule,
        }));
    }

    return data;
  });
